import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, Stack, FormControl, InputLabel, Select, MenuItem,
  TextField, IconButton, Card,
  Divider, // Added Card for mobile view
} from '@mui/material';
import { useResponsive } from '../../hooks/useResponsive'; // Added useResponsive import
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { IManualMovement, ManualMovementType } from '../../shared/types';

interface MovementsDialogProps {
  open: boolean;
  onClose: () => void;
  movements?: IManualMovement[];
  onRefresh?: () => void;
  onEdit?: (updatedMovement: IManualMovement) => void;
  onDelete?: (deletedId: string) => Promise<void>; // Make onDelete async
}

const formatNumber = (value: number | undefined, decimals = 2) => {
  if (typeof value !== 'number' || isNaN(value)) return '-';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/** UTC day of a movement date given as "MM/DD/YYYY" or ISO "YYYY-MM-DD…" (null if unreadable). */
const dayStamp = (value: string | number | undefined): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const us = typeof value === 'string' ? value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/) : null;
  const ts = us ? Date.UTC(Number(us[3]), Number(us[1]) - 1, Number(us[2])) : new Date(value).getTime();
  if (!Number.isFinite(ts)) return null;
  const d = new Date(ts);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

const MovementsDialog: React.FC<MovementsDialogProps> = ({
  open,
  onClose,
  movements = [],
  onEdit,
  onDelete,
}) => {
  const [selectedType, setSelectedType] = useState<ManualMovementType | 'ALL'>('ALL');
  const [selectedToken, setSelectedToken] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [editCell, setEditCell] = useState<{ id: string; field: keyof IManualMovement } | null>(null);
  const [toDelete, setToDelete] = useState<IManualMovement | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { isMobile } = useResponsive();

  const uniqueTokens = useMemo(() => {
    return Array.from(new Set(movements.map(m => m.token).filter(Boolean))) as string[];
  }, [movements]);

  const filteredMovements = useMemo(() => {
    return movements
      .filter(m => {
        const matchesType = selectedType === 'ALL' || m.movementType === selectedType;
        const matchesToken = selectedToken === 'ALL' || m.token === selectedToken;
        // Movement dates arrive as "MM/DD/YYYY" or ISO; the inputs are "YYYY-MM-DD": compare days.
        const day = dayStamp(m.date);
        const afterStart = startDate ? day !== null && day >= Date.parse(`${startDate}T00:00:00Z`) : true;
        const beforeEnd = endDate ? day !== null && day <= Date.parse(`${endDate}T00:00:00Z`) : true;
        return matchesType && matchesToken && afterStart && beforeEnd;
      })
      .sort((a, b) => {
        // 1. Parse dates to handle mixed formats (ISO YYYY-MM-DD and US MM/DD/YYYY)
        const timeA = new Date(a.date).getTime();
        const timeB = new Date(b.date).getTime();

        // 2. Sort by date timestamp (descending)
        if (timeA !== timeB && !isNaN(timeA) && !isNaN(timeB)) {
          return timeB - timeA;
        }

        // 3. Fallback: Sort by creation time if available (newest first)
        if (b.createdAt && a.createdAt) {
          return b.createdAt - a.createdAt;
        }

        // 4. Fallback: Sort by token (ascending)
        const tokenA = a.token || '';
        const tokenB = b.token || '';
        return tokenA.localeCompare(tokenB);
      });
  }, [movements, selectedType, selectedToken, startDate, endDate]);

  const handleBlur = (id: string, field: keyof IManualMovement, value: any) => {
    setEditCell(null);
    const original = movements.find(m => m.id === id);
    if (!original) return;
    // Save only a real change to a valid number: a blur without edits (or a cleared cell) must not
    // rewrite the movement, since the capital basis and P&L are computed from it.
    if (typeof value !== 'number' || !Number.isFinite(value) || value === original[field]) return;
    onEdit?.({ ...original, [field]: value });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        // Phones get the shared bottom-sheet dialog style from the theme.
      >
        <DialogTitle sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          Manual movements
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {!isMobile && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={selectedType} label="Type" onChange={(e) => setSelectedType(e.target.value as any)}>
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="DEPOSIT">Deposit</MenuItem>
                  <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                  <MenuItem value="MANUAL_PREMIUM">Manual Premium</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Token</InputLabel>
                <Select value={selectedToken} label="Token" onChange={(e) => setSelectedToken(e.target.value)}>
                  <MenuItem value="ALL">All</MenuItem>
                  {uniqueTokens.map(token => (
                    <MenuItem key={token} value={token}>{token}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          )}

          {filteredMovements.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No movements match the filters.
            </Typography>
          ) : (
            isMobile ? (
              <Stack spacing={2}>
                {filteredMovements.map((m, i) => (
                  <Card key={i} sx={{ p: 2, borderRadius: 2, border: '1px solid #424242' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={600}>Date: {m.date}</Typography>
                      <Divider />
                      <Typography variant="body2">Type: {m.movementType}</Typography>
                      <Typography variant="body2">Token: {m.token ?? '-'}</Typography>
                      <Typography variant="body2">Quantity: {formatNumber(m.quantity, 4)}</Typography>
                      <Typography variant="body2">Price: {formatNumber(m.price, 4)}</Typography>
                      <Typography variant="body2">Amount USD: {formatNumber(m.amountUSD, 2)}</Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                      </Stack>
                    </Stack>
                  </Card>
                ))}
                </Stack>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Token</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Amount USD</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMovements.map((m, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{m.date}</TableCell>
                      <TableCell>{m.movementType}</TableCell>
                      <TableCell>{m.token ?? '-'}</TableCell>
                      {(['quantity', 'price', 'amountUSD'] as const).map(field => (
                        <TableCell
                          key={field}
                          align="right"
                          onDoubleClick={() => setEditCell({ id: m.id, field })}
                          sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        >
                          {editCell?.id === m.id && editCell.field === field ? (
                            <TextField
                              size="small"
                              variant="standard"
                              autoFocus
                              type="number"
                              defaultValue={m[field] ?? ''}
                              onBlur={(e) => handleBlur(m.id, field, parseFloat(e.target.value))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const input = e.target as HTMLInputElement;
                                  handleBlur(m.id, field, parseFloat(input.value));
                                }
                              }}
                              inputProps={{ style: { textAlign: 'right', fontFamily: 'monospace' } }}
                            />
                          ) : (
                            formatNumber(m[field], field === 'amountUSD' ? 2 : 4)
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => setToDelete(m)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle sx={{ color: 'error.main' }}>Confirm deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this movement from {toDelete?.date}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} size="small" disabled={deleting}>Cancel</Button>
          <Button
            onClick={async () => {
              if (onDelete && toDelete) {
                setDeleting(true);
                try {
                  await onDelete(toDelete.id);
                } catch (error) {
                  console.error('Failed to delete movement', error);
                  // Optionally, show an error message to the user
                } finally {
                  setDeleting(false);
                  setToDelete(null);
                }
              }
            }}
            variant="contained"
            size="small"
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MovementsDialog;
